import SessionService from 'ember-simple-auth/services/session';
import { inject as service } from '@ember/service';
import { notifyPropertyChange } from '@ember/object';
import { STORAGE_KEYS } from 'sh-www/services/local-storage-fallback';
import config from 'sh-www/config/environment';
import { dropTask } from 'ember-concurrency';

class ShSessionService extends SessionService {
  // this has been renamed to prevent a namespace collision with an inherited property of
  // `SessionService`
  @service('store') internalStore;
  @service('local-storage-fallback') storage;
  @service('error-tracker') errorTracker;
  @service api;
  @service router;
  @service cookies;

  #currentUser = null;

  get currentUser() {
    return this.#currentUser;
  }

  get isMasquerading() {
    try {
      return !!this.storage.getItem(STORAGE_KEYS.MASQUERADE_ORIG_AUTH);
    } catch (error) {
      // localstorage can be disabled/not accessible by the client
      this.errorTracker.error('Local storage is unavailable', this, error);
      return false;
    }
  }

  async sessionHeaders(method) {
    let headers = { 'SH-Web-Version': config.SH_STATIC_VERSION };

    if (method !== 'GET' && method !== 'HEAD') {
      // XSRF
      let xsrfToken = this.cookies.read('XSRF-TOKEN');

      if (!xsrfToken) {
        await this.api.fetchXsrf();
        xsrfToken = this.cookies.read('XSRF-TOKEN');
      }

      headers = { ...headers, 'X-XSRF-TOKEN': xsrfToken };
    }

    return headers;
  }

  /** @inheritDoc */
  handleAuthentication() {
    if (config.DEBUG) {
      console.log(`SESSION - Handling authentication`);
    }

    // Do not call super to avoid unwanted transitions
  }

  /** @inheritDoc */
  handleInvalidation() {
    if (config.DEBUG) {
      console.log(`SESSION - Handling invalidation`);
    }

    window.location.assign('/signin?from=logout');
  }

  /**
   * @return {Promise<*>}
   */
  clearStore() {
    if (config.DEBUG) {
      console.log(`SESSION - Clearing store`);
    }

    this.#setCurrentUser(null);
    return this.session._clear(false);
  }

  /**
   * @return {Promise<*>}
   */
  async loadCurrentUser() {
    return this.loadCurrentUserTask.isRunning
      ? this.loadCurrentUserTask.last
      : this.loadCurrentUserTask.perform();
  }

  @dropTask
  *loadCurrentUserTask() {
    if (config.DEBUG) {
      console.log(`SESSION - Loading current user`);
    }

    const response = yield this.api.get('users/me', {
      params: {
        include: 'company,company-membership,payout-accounts,user-experiments',
      },
    });

    this.internalStore.pushPayload('user', response);

    return this.#setCurrentUser(
      this.internalStore.peekRecord('user', response.data.id),
    );
  }

  #setCurrentUser(user) {
    if (config.DEBUG) {
      console.log(`SESSION - Setting current user`);
    }

    if (this.#currentUser !== user) {
      this.#currentUser = user;
      notifyPropertyChange(this, 'currentUser');
    }

    return this.#currentUser;
  }
}

export default ShSessionService;
